import { useEffect, useMemo, useState } from 'react';
import {
  ConfirmPaymentTransactionRequest,
  dispatch,
  ErrorVariants,
  getCustomerInvite,
  getExtraDetails,
  getWorkspaceId,
  Invoice,
  PaymentDetailsState,
  showToast,
  useAppSelector,
  useConfirmPaymentTransactionMutation,
  useLazyGetPaymentDetailsByIdQuery,
} from '@zonofi/zono-money-store';
import { useParams } from 'react-router-dom';
import { useMappedDetails } from './use-mapped-details';
import { convertToISTDateTime } from '../../helper';
import { PaymentStatusEnum } from '../../../constants';
import { onClickBackToWhatsapp } from '../../../helper';
import { FileUploadOutput } from '@zonofi/common/types/interface';

export function usePaymentDetails() {
  const { paymentId } = useParams();
  const customerData = useAppSelector(getExtraDetails);
  const sellerWorkspaceId = useAppSelector(getWorkspaceId);
  const inviteDetails = useAppSelector(getCustomerInvite);
  const { seller, waConfig } = inviteDetails;
  const [refreshNonce, setRefreshNonce] = useState(0);

  const { detailsDataMapping } = useMappedDetails();
  const [getPaymentDetailsById, paymentDetailsByIdResponse] =
    useLazyGetPaymentDetailsByIdQuery();
  const [confirmPaymentTransactionAPI, confirmPaymentTransactionAPIResponse] =
    useConfirmPaymentTransactionMutation();

  const [isLoading, setisLoading] = useState(true);
  const [successState, setSuccessState] = useState(false);
  const [showThreeDotModal, setShowThreeDotModal] = useState(false);
  const [modalState, setModalState] = useState({
    showOption: false,
    showImage: false,
  });
  const [selectedMedia, setSelectedMedia] = useState<FileUploadOutput>();
  const [paymentCustInfo, setPaymentCustInfo] = useState({
    paymentRefCode: '',
    customerName: '',
    status: '',
  });
  const [mappedPaymentDetails, setMappedPaymentDetails] =
    useState<PaymentDetailsState>({
      paymentDetails: [],
      paymentMedia: [],
      paymentInvoice: [],
    });

  useEffect(() => {
    if (paymentId) {
      getPaymentDetailsById({
        paymentId,
        includeSettlement: true,
        workspaceId: sellerWorkspaceId,
        sellerWorkspaceId,
      });
    }
  }, [paymentId, refreshNonce]);

  useEffect(() => {
    if (
      paymentDetailsByIdResponse.isSuccess &&
      paymentDetailsByIdResponse.data &&
      customerData?.customerName
    ) {
      const { mappedDetails } = detailsDataMapping({
        paymentData: paymentDetailsByIdResponse?.data,
      });

      setMappedPaymentDetails(mappedDetails);
      setPaymentCustInfo((prev) => ({
        ...prev,
        paymentRefCode: paymentDetailsByIdResponse?.data?.paymentRefCode ?? '',
        status: paymentDetailsByIdResponse?.data?.status ?? '',
        customerName: customerData?.customerName ?? '',
      }));
      setisLoading(false);
    }
  }, [paymentDetailsByIdResponse, customerData?.customerName]);

  const isPermittedToAcceptReject = useMemo(() => {
    return paymentDetailsByIdResponse.data?.status === PaymentStatusEnum.SENT;
  }, [paymentDetailsByIdResponse.data?.status]);

  const createPaymentPayload = (
    paymentDetails: Record<string, string>,
    invoiceDetail: Invoice[]
  ) => {
    const paymentDate = convertToISTDateTime(
      paymentDetails.paymentDate ?? '',
      paymentDetails.paymentTime ?? ''
    );

    const invoices =
      Array.isArray(invoiceDetail) && invoiceDetail.length > 0
        ? invoiceDetail.map((item) => ({
            docNumber: item?.docNumber,
            doctype: 'PYMNT',
            referenceInvoiceNumber: item?.referenceInvoiceNumber,
          }))
        : [];

    const {
      inviteId,
      createdBy,
      amountPaid,
      paymentMode,
      bankReference,
      paymentRefCode,
      bankName,
      bankAccountNumber,
      upiId,
      paymentType,
      narration,
    } = paymentDetailsByIdResponse?.data || {};

    return {
      sellerWorkspaceId,
      buyerWorkspaceId: '',
      inviteId,
      createdBy,
      paymentDate,
      amountPaid,
      currencyCode: 'INR',
      paymentMode,
      bankReference,
      paymentRefCode,
      bankName,
      bankAccountNumber,
      upiId,
      paymentType,
      narration,
      doctype: 'PYMNT',
      invoices,
    };
  };

  const onAcceptPress = async () => {
    const paymentDetailsArray = mappedPaymentDetails.paymentDetails;
    const invoiceDetail = mappedPaymentDetails.paymentInvoice;
    if (!paymentDetailsArray || paymentDetailsArray.length === 0) return;
    const paymentDetailsMap = paymentDetailsArray.reduce(
      (acc, { key, value }) => {
        acc[key] = value ?? '';
        return acc;
      },
      {} as Record<string, string>
    );

    const payload = createPaymentPayload(paymentDetailsMap, invoiceDetail);
    await confirmPaymentTransactionAPI(
      payload as ConfirmPaymentTransactionRequest
    );
  };

  useEffect(() => {
    if (confirmPaymentTransactionAPIResponse.isSuccess) {
      setSuccessState(true);
    } else if (confirmPaymentTransactionAPIResponse.isError) {
      dispatch(
        showToast({
          message: 'Something went wrong',
          type: ErrorVariants.DANGER,
          canDisplay: true,
          responseStatus: 'ERROR',
        })
      );
    }
  }, [confirmPaymentTransactionAPIResponse]);

  const onRejectPress = () => {};

  const onWhatsAppPress = () => {
    onClickBackToWhatsapp({
      whatsappPhoneNo: waConfig?.whatsappPhoneNo,
      workspaceId: seller?.workspaceId,
    });
  };

  const onThreeDotPress = (media: FileUploadOutput) => {
    setSelectedMedia(media);
    setShowThreeDotModal(true);
    setModalState({ showOption: true, showImage: false });
  };

  const refresh = () => {
    setRefreshNonce((prev) => prev + 1);
  };

  return {
    paymentCustInfo,
    mappedPaymentDetails,
    isPermittedToAcceptReject,
    onAcceptPress,
    onRejectPress,
    acceptLoader: confirmPaymentTransactionAPIResponse?.isLoading,
    setSuccessState,
    successState,
    onWhatsAppPress,
    onThreeDotPress,
    setShowThreeDotModal,
    showThreeDotModal,
    selectedMedia,
    modalState,
    setModalState,
    isLoading: isLoading || paymentDetailsByIdResponse.isLoading,
    paymentId,
    response: paymentDetailsByIdResponse?.data,
    refresh,
  };
}
